import { useMemo, useState } from "react";
import { BarChart3 } from "lucide-react";
import { DEFAULT_RANGE, filterLeadsByRange, RANGE_OPTIONS } from "@/lib/leadsFilter";
import TimeRangeFilter from "@/components/admin/TimeRangeFilter";

/**
 * Aggregates leads by `service` field and renders a horizontal bar chart.
 * Recognizes both:
 *   - `service-detail-<slug>`  → embedded form on SEO subpage
 *   - `<slug>` (e.g. starlink, wifi, smart-home, creator, support, digital) → main contact form
 *   - null/empty → "Ohne Angabe"
 */

const SLUG_LABELS = {
  starlink: "Starlink",
  wifi: "Premium WLAN",
  smart: "Smart Home",
  "smart-home": "Smart Home",
  digital: "Digitale Services",
  creator: "Creator & Streaming",
  support: "Tech Support",
};

const prettify = (raw) => {
  if (!raw) return { label: "Ohne Angabe", source: "Direkt" };
  const isDetail = raw.startsWith("service-detail-");
  const slug = isDetail ? raw.replace("service-detail-", "") : raw;
  const label = SLUG_LABELS[slug] || slug;
  return {
    label,
    source: isDetail ? "SEO-Unterseite" : "Hauptformular",
  };
};

const LeadsSourcesChart = ({ leads }) => {
  const [range, setRange] = useState(DEFAULT_RANGE);

  const filtered = useMemo(() => filterLeadsByRange(leads || [], range), [leads, range]);
  const rangeLabel = RANGE_OPTIONS.find((r) => r.key === range)?.label || "";

  const rows = useMemo(() => {
    const map = new Map();
    for (const l of filtered) {
      const { label, source } = prettify(l.service);
      const key = `${label}__${source}`;
      const cur = map.get(key) || { label, source, count: 0 };
      cur.count += 1;
      map.set(key, cur);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => b.count - a.count);
    return arr;
  }, [filtered]);

  const total = rows.reduce((a, b) => a + b.count, 0);
  const max = Math.max(1, ...rows.map((r) => r.count));

  return (
    <div
      data-testid="leads-sources-chart"
      className="rounded-3xl bg-white border border-neutral-200 p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-2xl bg-neutral-100 grid place-items-center text-neutral-700">
            <BarChart3 className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-display text-lg font-medium">Leads pro Quelle</h2>
            <div className="text-xs text-neutral-500">
              Welche Service-Seite konvertiert am besten?
            </div>
          </div>
        </div>
        <TimeRangeFilter
          value={range}
          onChange={setRange}
          testId="chart-range-filter"
        />
      </div>

      <div className="mt-4 text-xs text-neutral-400">
        {total} {total === 1 ? "Lead" : "Leads"} im Zeitraum „{rangeLabel}"
      </div>

      {rows.length === 0 ? (
        <div className="mt-6 text-sm text-neutral-500" data-testid="leads-sources-empty">
          Keine Daten im gewählten Zeitraum.
        </div>
      ) : (
        <ul className="mt-6 space-y-4">
          {rows.map((r) => {
            const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
            const widthPct = Math.max(4, (r.count / max) * 100); // min visible width
            const isSeo = r.source === "SEO-Unterseite";
            return (
              <li
                key={`${r.label}-${r.source}`}
                data-testid={`leads-source-row-${r.label}-${r.source}`}
                className="space-y-1.5"
              >
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-neutral-900 truncate">{r.label}</span>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0 ${
                        isSeo
                          ? "bg-sky-50 text-sky-700"
                          : "bg-neutral-100 text-neutral-600"
                      }`}
                    >
                      {r.source}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-neutral-500">{pct}%</span>
                    <span className="text-sm font-medium tabular-nums text-neutral-900">
                      {r.count}
                    </span>
                  </div>
                </div>
                <div className="h-2 w-full rounded-full bg-neutral-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      isSeo
                        ? "bg-gradient-to-r from-sky-500 to-sky-700"
                        : "bg-gradient-to-r from-neutral-700 to-neutral-950"
                    } transition-[width] duration-500`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LeadsSourcesChart;
